import { BLOCKS } from '@contentful/rich-text-types';
import { graphql } from 'gatsby';
import get from 'lodash.get';
import React, { useEffect, useState } from 'react';

import { PrimaryLink } from '../components/Common/Links';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import PageSection from '../components/PageSections/PageSection';
import RageHint from '../components/ScavengerHunt/Hints/RageHint';
import SentimentModal from '../components/ScavengerHunt/SentimentModal';
import * as styles from './error.module.scss';

const loadEmbedScript = (callback) => {
  const existingScript = document.getElementById('confetti-run');
  if (!existingScript) {
    const script = document.createElement('script');
    script.src = '/js/confettiRun.js';
    script.id = 'confetti-run';
    script.async = true;
    document.head.appendChild(script);
    script.onload = () => {
      if (callback) callback();
    };
  }
  if (existingScript && callback) callback();
};

const Error = ({ data, pageContext }) => {
  const [showSentimentModal, setShowSentimentModal] = useState(false);
  let page = {};
  if (data.page) page = data.page;

  const image = page.heroImage?.file.url;

  const [loadedScript, setLoadedScript] = useState(false);
  useEffect(() => {
    if (loadedScript) return;
    loadEmbedScript(() => setLoadedScript(true));
  }, [loadedScript]);

  // Special case for digital scavenger hunt
  const handleClick = () => {
    setShowSentimentModal(true);
  };

  // TODO: We need to revisit this to see if it's worth running through
  // the section builder
  const options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const type = node?.data?.target?.internal?.type;

        // Rage Click Button
        if (type === 'ContentfulComponentCallToAction') {
          const buttonType = get(node, 'data.target.primaryCtaStyle');
          const url = get(node, 'data.target.url');
          const buttonText = get(node, 'data.target.ctaLabel');
          if (buttonType === 'Rage Click Button') {
            return (
              <>
                <RageHint />
                <PrimaryLink
                  style={{ position: 'relative' }}
                  id="rage-click"
                  label={buttonText}
                  isButton
                  onClick={handleClick}
                />
              </>
            );
          }
          return <PrimaryLink label={buttonText} url={url} isButton={true} />;
        }

        // Rich Text Image
        if (type === 'ContentfulComponentSimpleContent') {
          const asset = get(node, 'data.target');
          return <img className={styles.headingImage} src={asset.image.file.url} alt={asset.title || ''} />;
        }
      },
    },
  };

  return (
    <Layout isDach={pageContext.isDach}>
      <PageSection isTopSection>
        <div className={image ? styles.grid : styles.gridNoImage}>
          <div className={image ? styles.copyWithImage : styles.copy}>
            <RichTextConverter options={options} copy={get(page, 'body')} />
          </div>
          <div className={styles.image}>
            {image && <img src={image} loading="lazy" alt={get(page, 'heroImage.file.title', '')} />}
          </div>
        </div>
      </PageSection>
      <SentimentModal isOpen={showSentimentModal} setShowModal={setShowSentimentModal} sentiment="rage" />
    </Layout>
  );
};

export default Error;

export const Head = ({ data: { page }, location }) => (
  <>
    <MarketingSeo meta={page.pageMetaData} slug={location.pathname} />
    <script src="/js/confettiKit.js" />
  </>
);

export const query = graphql`
  query ErrorQuery($slug: String!) {
    page: contentfulPageSimple(slug: { eq: $slug }) {
      pageTitle
      body {
        raw
        references {
          ... on Node {
            ...CallToAction
            ...SimpleCard
          }
        }
      }
      heroImage {
        file {
          url
        }
        title
      }
      pageMetaData {
        ... on Node {
          ...PageMetaData
        }
      }
    }
  }
`;
